import type { ReactNode } from "react";

import dayjs from "dayjs";
import { identity } from "lodash";
import styled from "styled-components";

import {
    GroupTrafficShare,
    TrafficShareWithVisits,
    LinkCell,
    WaKeywordPosition,
    WaKeywordPositionCompare,
    SearchKeywordCell,
} from "app/components/React/Table/cells";
import { ChangeStateCell } from "app/components/React/Table/cells/ChangeStateCell";
import { CompareBarCell } from "app/components/React/Table/cells/CompareBarCell";
import { NewAndLostChangeCell } from "app/components/React/Table/cells/NewAndLostChangeCell";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import {
    capitalizeFirstLetterFilter,
    abbrNumberVisitsFilter,
    minVisitsAbbrFilter,
    percentageSignFilter,
} from "app/filters/ngFilters";
import { UNDEFINED_SIGN } from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import DurationService, { apiFormat, customRangeFormat } from "app/services/DurationService";
import { TableWidget } from "./TableWidget";

import WebsiteKeywordsDataFetcherFactory from "../widget-fetchers/WebsiteKeywordsDataFetcherFactory";
import { TableTemplateV2 } from "../dashboard/TableTemplate-v2";

import { TableWebsiteKeywordsDashboardWidgetFilters } from "./TableWebsiteKeywordsDashboardWidgetFilters";
import { WidgetFilter } from "./TableLandingPageDashboardWidget";
import type { ISubtitleTextItem } from "./Widget";
import {
    WebsiteKeywordsTableWidgetAPIParams,
    WebsiteKeywordsWidgetFiltersMap,
} from "./TableWebsiteKeywordsDashboardConsts";

import { WebsiteKeywordsTableTabSubtitle } from "../widget-subtitle-templates/WebsiteKeywordsTableTabSubtitle";

import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import type { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { allTrackers } from "app/services/track/track";

const WaKeywordPositionStyled = styled(WaKeywordPosition)`
    text-align: left;
`;

const NewAndLostChangeCellStyled = styled(NewAndLostChangeCell)`
    text-align: left;
`;

const ChangeStateCellStyled = styled(ChangeStateCell)`
    text-align: left;
`;

const WaKeywordPositionCompareStyled = styled(WaKeywordPositionCompare)`
    text-align: left;
`;
const AlignRight = styled.div`
    text-align: right;
`;
const getChangeCellComponent = (change, value) => {
    const absChange = Math.abs(change);
    const precision = absChange > 0 && absChange < 0.1 ? 1 : 0;
    return (
        <ChangeStateCellStyled
            value={value}
            undefinedSign={"-"}
            isPercentage={true}
            cellFilter={`simplePercentage:${precision}`}
        />
    );
};

export function getFiltersTooltip(mappedFilters: { divElements: ReactNode; divCount: number }[]): ISubtitleTextItem {
    const availableFilters = mappedFilters.map((filter) => filter?.divElements);

    const calculateTotalDivCount = (accumulator, currentObject) => {
        if (currentObject && currentObject.divCount) {
            return accumulator + currentObject.divCount;
        }
        return accumulator;
    };

    const amountOfFilters = mappedFilters.reduce(calculateTotalDivCount, 0);

    if (!amountOfFilters) {
        return;
    }

    const filtersTextSubtitle =
        amountOfFilters > 1
            ? translate("dashboard.widget.subtitle.landing.page.filters.multiple", {
                  amount: amountOfFilters,
              })
            : amountOfFilters === 1
              ? translate("dashboard.widget.subtitle.landing.page.filters.unique")
              : availableFilters[0];

    const tooltipValue = availableFilters.map((filter, index) => <WidgetFilter key={index}>{filter}</WidgetFilter>);
    return {
        text: filtersTextSubtitle,
        tooltipValue,
    };
}

export class TableWebsiteKeywordsDashboardWidget extends TableWidget {
    constructor() {
        super();
    }

    public getSearchKey() {
        return "SearchTerm";
    }

    runWidget() {
        if (this._autoFetchData) {
            this.getData();
        }
        this.setMetadata();
        this.getLegendImage();
    }

    static getWidgetMetadataType() {
        return "TableWebsiteKeywordsDashboard";
    }

    static getWidgetDashboardType() {
        return "Table";
    }

    static getWidgetResourceType() {
        return "Table";
    }

    protected getDataFetcherFactory() {
        return WebsiteKeywordsDataFetcherFactory;
    }

    static getFiltersComponent() {
        return TableWebsiteKeywordsDashboardWidgetFilters;
    }

    get templateComponent() {
        return TableTemplateV2;
    }

    protected rowReducer(row) {
        const requestParams = this._params;
        const selectedMonth = dayjs.utc(requestParams.to, apiFormat).format(customRangeFormat);
        const is28Days = requestParams.latest === "28d";

        const stateParams = {
            isWWW: "*",
            duration: is28Days ? "28d" : `${selectedMonth}-${selectedMonth}`,
            country: requestParams.country,
            keyword: row.SearchTerm,
            webSource: requestParams.webSource,
        };

        return {
            ...row,
            url: this._swNavigator.href("organicsearch_websites_v2_traffic_keywords", stateParams, {}),
        };
    }

    async getViewData() {
        super.getViewData();

        const webSource = this._widgetConfig.properties.webSource;
        if (!this._widgetConfig.properties.country && !webSource) {
            //in case devices was not selected yet
            return this.viewData;
        }
        const mappedFilters = await Promise.all(
            WebsiteKeywordsTableWidgetAPIParams.map((filter) => WebsiteKeywordsWidgetFiltersMap[filter](this._params)),
        );
        this.viewData.subtitleTextItem = getFiltersTooltip(mappedFilters);
        this.viewData.customSubtitle = "true";
        this.viewData.customSubtitleComponent = WebsiteKeywordsTableTabSubtitle;
        return this.viewData;
    }

    getSingleHeader = () => this.subtitleData;

    getSingleHeaderItem = () => this.subtitleData && this.subtitleData.TotalClicks;

    getAbbrNumberVisitsFilter = () => abbrNumberVisitsFilter();

    getMinVisitsAbbrFilter = () => minVisitsAbbrFilter();

    getPercentagesignFilter = () => percentageSignFilter();

    getColumnsConfig() {
        const compare = this.isCompare();
        const { country, keys, latest, sourceType, isWindow, webSource, to, from } = this.apiParams;
        const is28Days = latest === "28d";
        const isTotalTabSelected = sourceType === SourceTypeMap.Total;
        const onKeywordClick = (value: string) => {
            allTrackers.dispatchTrackEvent("Internal Link", "click", `table/${value || ""}`);
        };

        const columns = [
            {
                name: "Keyword",
                title: translate("keywords.by.website.table.columns.keyword.title"),
                cellComponent: (props) => {
                    return (
                        <SearchKeywordCell
                            {...props}
                            onItemClick={onKeywordClick}
                            destinationState={"organicsearch_keywordAnalysis_2"}
                            destinationStateParams={{
                                selectedPageTab: capitalizeFirstLetterFilter()(sourceType),
                            }}
                            navigatorParams={{
                                webSource: webSource,
                                country: country,
                                isWWW: isWindow,
                                duration: DurationService.getMonthsFromApiDuration(from, to, true),
                            }}
                        />
                    );
                },
                width: "177px",
            },
            {
                name: "Share",
                title: translate(
                    `keywords.by.website.table.columns.${sourceType?.toLowerCase()}.clicks.dashboard.title`,
                ),
                width: "170px",
                cellComponent: (props) => (
                    <TrafficShareWithVisits
                        {...props}
                        applyAbbrNumberFilter
                        totalVisitsRowProp="Clicks"
                        thresholdForTotalFilter={50}
                    />
                ),
            },
            {
                name: "ClicksChange",
                title: translate("analysis.source.website.keywords.table.columns.change.title"),
                cellTemp: "change-percentage",
                cellComponent: (props) => {
                    const { change, ClicksChange } = props.row;
                    return getChangeCellComponent(change, ClicksChange);
                },
                format: "percentagesign",
                width: "116px",
                ppt: {
                    overrideFormat: "smallNumbersPercentage:2",
                },
            },
            compare
                ? {
                      name: "SiteOrigins",
                      title: translate("analysis.source.search.all.table.columns.shareCompare.title"),
                      cellComponent: (props) => {
                          const keysArray: string[] = keys.split(",");
                          const value = Object.entries(props.value).map(([site, count]) => ({
                              name: site,
                              value: count,
                              color: CHART_COLORS.compareMainColors[keysArray.indexOf(site)],
                          }));
                          return (
                              <GroupTrafficShare
                                  {...props}
                                  value={value}
                              />
                          );
                      },
                      width: "198px",
                  }
                : null,
            ...(!is28Days
                ? [
                      {
                          name: "KwVolume",
                          title: translate("analysis.source.search.all.table.columns.volume.title"),
                          format: "minAbbrNumber:50",
                          width: "104px",
                      },
                  ]
                : [
                      {
                          name: "KwWindowVolume",
                          title: translate("website.keywords.2.0.table.window.volume.column"),
                          format: "minAbbrNumber:50",
                          width: "104px",
                      },
                  ]),
            {
                name: "KwVolumeAverage",
                title: translate("analysis.source.search.all.table.columns.average.volume.title"),
                format: "minAbbrNumber:50",
                minWidth: "104px",
            },
            isTotalTabSelected
                ? {
                      name: "OrganicClicksShare",
                      title: translate("mobile.v2.table.columns.sourceType.share.title"),
                      cellComponent: (props) => {
                          if (props.row.Clicks > 1) {
                              return (
                                  <CompareBarCell
                                      shouldAddRightSpace={true}
                                      isSourceType={true}
                                      {...props}
                                  />
                              );
                          } else {
                              return <AlignRight>{"-"}</AlignRight>;
                          }
                      },
                      tooltip: translate("website.keywords.2.0.table.sourceType.column.tooltip"),
                      minWidth: "144px",
                  }
                : null,

            ...(country !== "999"
                ? [
                      {
                          name: "Position",
                          title: translate("website.keywords.2.0.page.table.position.column.title"),
                          cellComponent: compare
                              ? (props) => {
                                    return (
                                        <WaKeywordPositionCompareStyled
                                            {...props}
                                            topSiteColor={props.value[0].Color}
                                        />
                                    );
                                }
                              : WaKeywordPositionStyled,
                          format: "minusNAFilter",
                          width: "90px",
                      },
                      ...(!compare
                          ? [
                                {
                                    name: "SitesData",
                                    cellComponent: (props) => {
                                        const { Position, PreviousPosition } = props.value?.[0];
                                        const PositionChangeObj = {
                                            first: PreviousPosition,
                                            last: Position,
                                            change: PreviousPosition - Position,
                                        };
                                        return (
                                            <NewAndLostChangeCellStyled
                                                {...props}
                                                undefinedSign={UNDEFINED_SIGN}
                                                isPercentage={false}
                                                value={PositionChangeObj}
                                            />
                                        );
                                    },
                                    cellFilter: "number:0",
                                    title: translate("keywords.by.website.table.columns.pos.change.title"),
                                    width: "90px",
                                },
                            ]
                          : []),
                  ]
                : []),
            {
                name: "Cpc",
                title: translate("analysis.source.search.all.table.columns.cpc.title"),
                tooltip: translate("website.keywords.2.0.table.cpc.column.tooltip"),
                format: "CPC",
                width: "90px",
            },
            {
                name: "TopUrl",
                title: translate("keywords.by.website.table.columns.top.url.title"),
                cellComponent: LinkCell,
                cellClass: "url-cell",
            },
        ].filter(identity);

        return columns;
    }

    public getProUrl(rowParams) {
        const {
            country,
            webSource,
            from,
            to,
            latest,
            key,
            keys,
            family,
            source,
            pageFilter,
            multiIncludeExcludeWebsites,
            multiIncludeExcludeKeywords,
            Change,
            IncludeBranded,
            IncludeNoneBranded,
            rangeFilter,
            sourceType,
            cpcFromValue,
            cpcToValue,
            volumeFromValue,
            volumeToValue,
            trafficPreset,
        } = this.apiParams;

        const is28Days = latest === "28d";
        const sourceKey = Object.keys(SourceTypeMap).find(
            (key) => SourceTypeMap[key as keyof typeof EKeywordTabs] === sourceType,
        );
        const { duration } = this._widgetConfig?.properties;

        const addFieldToRules = (rulesString: string, field: string) => {
            if (!rulesString) return rulesString;
            try {
                const rules = JSON.parse(rulesString);
                return JSON.stringify(rules.map((rule) => ({ ...rule, field })));
            } catch {
                return rulesString;
            }
        };

        const toParams = {
            country,
            duration: is28Days ? "28d" : duration,
            webSource,
            key: key ?? keys,
            pageFilter,
            family,
            source,
            multiIncludeExcludeWebsites,
            multiIncludeExcludeKeywords: addFieldToRules(multiIncludeExcludeKeywords, "keyword"),
            Change,
            IncludeBranded,
            IncludeNoneBranded,
            selectedPageTab: sourceKey,
            rangeFilter,
            cpcFromValue,
            cpcToValue,
            volumeFromValue,
            volumeToValue,
            trafficPreset,
            ...(is28Days ? { latest: "28d" } : {}),
        };

        return this._swNavigator.getStateUrl("organicsearch_websites_v2_traffic_keywords", {
            ...toParams,
        });
    }
}

TableWebsiteKeywordsDashboardWidget.register();
